import { useActiveUrlStore } from '@/store/activeUrlStore';
import { fetchPageSourceByUrl } from '@/services/scrapeFetchService';
import { useUrlSourcesStore } from '@/store/urlSourcesStore';
import { computed, watch } from 'vue';
import Provider from '@/enums/Provider';
import { useNotificationStore } from '@/store/notificationStore';
import { useSettingsStore } from '@/store/settings/settingsStore';
import { extractHomePageUrl } from '@/composables/useExtractUrls';
import { getBaseDomain } from '@/helpers/getBaseDomain';
import { parseUrlsFromSitemapXml } from '@/utils/parseUrlsFromSitemapXml';
import { parsePageSourceResponse } from '@/helpers/parsePageSourceResponse';

import { GoogleAnalytics } from '@/services/analytics/googleAnalytics';

export default function useFetchSitemap() {
  // Initialize stores
  const activeUrlStore = useActiveUrlStore();
  const urlSourcesStore = useUrlSourcesStore();
  const notificationStore = useNotificationStore();

  // Update computed value and watcher
  const settingsStore = useSettingsStore();
  const provider = computed(() => urlSourcesStore.getHomepageProvider);

  // Watch for provider changes and handle sitemap fetching
  watch(
    provider,
    async (newProvider) => {
      /* Ignore the early run that fires before we know the real provider */
      if (newProvider === Provider.OTHER || newProvider == null) return;
      const newUrl = activeUrlStore.activeUrl;
      if (!newUrl) return; // Exit if no active URL

      try {
        const sitemaps: string[] = []; // Store raw XML contents of all sitemaps
        // If the provider is ERS and the sitemap setting is enabled, add a placeholder causing it to skip looking for the sitemap.
        if ((newProvider === Provider.ERS || newProvider === Provider.IO) && settingsStore.getIsUseSitemap === true) {
          GoogleAnalytics.sitemapSkipped();
          extractHomePageUrl();
          notificationStore.addSuccessNotification('Skipping sitemap fetch for ERS or IO provider.');
          return;
        }
        if (newProvider === Provider.KBS || newProvider === Provider.ERS) {
          const sitemapUrl = new URL(newUrl).origin + '/sitemap.xml';
          const { data, statusCode } = await fetchPageSourceByUrl(sitemapUrl);
          if (statusCode === 200) {
            const parsedData = parsePageSourceResponse(data as string);
            if (parsedData.status === 'success' && parsedData.data.httpResponse) {
              sitemaps.push(parsedData.data.httpResponse);
              if (settingsStore.getIsDebugMode) notificationStore.addSuccessNotification(`Fetched sitemap: ${sitemapUrl}`);
            } else {
              GoogleAnalytics.sitemapNotFound();
            }
          } else {
            GoogleAnalytics.sitemapFailed();
          }
        }
        // Handle IO provider with sitemap index
        else if (newProvider === Provider.IO) {
          const sitemapIndexUrl = new URL(newUrl).origin + '/sitemap_index.xml';
          const { data: indexData, statusCode: indexStatus } =
            await fetchPageSourceByUrl(sitemapIndexUrl);

          // Validate index response
          if (indexStatus !== 200) {
            GoogleAnalytics.sitemapFailed();
            notificationStore.addErrorNotification(`Failed to fetch sitemap index: ${sitemapIndexUrl}`);
            extractHomePageUrl();
            return;
          }

          const parsedIndex = parsePageSourceResponse(indexData as string);
          if (parsedIndex.status !== 'success' || !parsedIndex.data.httpResponse) {
            if (settingsStore.getIsDebugMode) notificationStore.addErrorNotification(`Invalid sitemap index response: ${sitemapIndexUrl}`);
            extractHomePageUrl();
            return;
          } else {
            GoogleAnalytics.sitemapNotFound();
          }

          // Parse XML index using proper namespace
          const parser = new DOMParser();
          const indexXml = parser.parseFromString(parsedIndex.data.httpResponse, 'text/xml');
          const sitemapElements = indexXml.getElementsByTagNameNS(
            'http://www.sitemaps.org/schemas/sitemap/0.9',
            'sitemap'
          );

          // Extract and validate sitemap URLs from index
          const sitemapUrls = Array.from(sitemapElements)
            .map(sitemap => {
              // Use namespace-aware XML parsing to extract loc URLs
              const loc = sitemap.getElementsByTagNameNS(
                'http://www.sitemaps.org/schemas/sitemap/0.9',
                'loc'
              )[0]?.textContent;
              return loc ? loc.trim() : null;
            })
            .filter(Boolean) as string[];

          // Process each sitemap sequentially (maintains order)
          for (const sitemapUrl of sitemapUrls) {
            try {
              const { data: sitemapData, statusCode: sitemapStatus } =
                await fetchPageSourceByUrl(sitemapUrl);

              if (sitemapStatus === 200) {
                const parsedSitemap = parsePageSourceResponse(sitemapData as string);
                if (parsedSitemap.status === 'success' && parsedSitemap.data.httpResponse) {
                  sitemaps.push(parsedSitemap.data.httpResponse); // Add valid sub-sitemap
                }
              }
            } catch (error) {
              GoogleAnalytics.sitemapFailed();
              if (settingsStore.getIsDebugMode) notificationStore.addErrorNotification(`Error processing sitemap ${sitemapUrl}: ${error}`);
            }
          }
        }

        const normalizedBaseDomain = getBaseDomain(newUrl);
        const allLocs = sitemaps.flatMap(parseUrlsFromSitemapXml);
        const baseUrlExists = allLocs.some(
          (loc) => getBaseDomain(loc) === normalizedBaseDomain,
        );
        // Combine all sitemap contents and update store
        if (!baseUrlExists) {
          if (settingsStore.getIsDebugMode) notificationStore.addErrorNotification(`Base URL ${newUrl} not found in sitemap. Processing stopped.`);
          extractHomePageUrl();
          return;
        }
        if (sitemaps.length > 0) {
          // Merge sitemaps with newline separator to prevent XML concatenation issues
          urlSourcesStore.addSitemap(sitemaps.join('\n'));
        }
      } catch (error) {
        GoogleAnalytics.sitemapFailed();
        if (settingsStore.getIsDebugMode) notificationStore.addErrorNotification(`Error fetching sitemap: ${error}`);
        extractHomePageUrl();
      }
    },
    { immediate: true }
  );
}
